export type ApiAvailability = "ok" | "slow" | "unreachable";

// A request that is merely slow gets a soft, reversible warning; one that never
// settles is treated as an outage. Both thresholds are generous on purpose — the
// screen they drive blocks the whole return flow.
const SLOW_AFTER_MS = 8_000;
const UNREACHABLE_AFTER_MS = 25_000;

// These fetch clients also serve the whole back-office, where a slow export or a
// 500 on an admin route must never put the customer widget behind a maintenance
// screen. Only the widget's own endpoints count.
function isWidgetRequest(url: string): boolean {
  return url.includes("/public/");
}

export function requestUrl(input: unknown): string {
  if (typeof input === "string") return input;
  if (input instanceof Request) return input.url;
  if (input instanceof URL) return input.href;
  return "";
}

let _unreachable = false; // sticky: only a reload clears it
let _slowRequests = 0;
const _listeners = new Set<() => void>();

function _snapshot(): ApiAvailability {
  if (_unreachable) return "unreachable";
  return _slowRequests > 0 ? "slow" : "ok";
}

// Cached so getApiAvailability() is referentially stable for useSyncExternalStore.
let _state: ApiAvailability = _snapshot();

function _sync() {
  const next = _snapshot();
  if (next === _state) return;
  _state = next;
  _listeners.forEach((listener) => listener());
}

export function getApiAvailability(): ApiAvailability {
  return _state;
}

export function subscribeApiAvailability(listener: () => void): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

// Sticky on purpose: once we've decided the API is down, a later 2xx must not flip
// the widget back to the flow mid-render while half the queries are still failing.
export function reportApiStatus(url: string, status: number): void {
  if (status < 500 || !isWidgetRequest(url)) return;
  _unreachable = true;
  _sync();
}

export function reportApiNetworkError(url: string): void {
  if (!isWidgetRequest(url)) return;
  _unreachable = true;
  _sync();
}

// Watchdog for a hanging API: nothing here aborts the request. Aborting would risk
// reporting a failure for a return request the backend actually created, so we only
// observe and let the socket live. Call the returned function when the request
// settles, whatever the outcome.
export function reportRequestStart(url: string): () => void {
  if (!isWidgetRequest(url)) return () => {};

  let countedSlow = false;

  const slowTimer = setTimeout(() => {
    countedSlow = true;
    _slowRequests += 1;
    _sync();
  }, SLOW_AFTER_MS);

  const unreachableTimer = setTimeout(() => {
    _unreachable = true;
    _sync();
  }, UNREACHABLE_AFTER_MS);

  return () => {
    clearTimeout(slowTimer);
    clearTimeout(unreachableTimer);
    if (!countedSlow) return;
    _slowRequests -= 1;
    _sync();
  };
}
